import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { dashboardAPI } from '../lib/api';
import { toast } from 'sonner';
import { 
  Cog, 
  Factory, 
  Cpu, 
  Wrench, 
  Package, 
  Boxes, 
  MessageSquare, 
  Users,
  Loader2,
  AlertCircle
} from 'lucide-react';

const Dashboard = () => {
  const [counts, setCounts] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    fetchCounts();
  }, []);

  const fetchCounts = async () => {
    try {
      const response = await dashboardAPI.getCounts();
      setCounts(response.data);
    } catch (error) {
      toast.error('Failed to load dashboard data');
    } finally {
      setIsLoading(false);
    }
  };

  const statCards = counts ? [
    {
      label: 'Machine Types',
      value: counts.machine_types,
      icon: Cog,
      path: '/master/machine-types',
      color: 'text-blue-600',
      bgColor: 'bg-blue-50',
    },
    {
      label: 'Manufacturers',
      value: counts.manufacturers,
      icon: Factory,
      path: '/master/manufacturers',
      color: 'text-emerald-600',
      bgColor: 'bg-emerald-50',
    },
    {
      label: 'Models',
      value: counts.models,
      icon: Cpu,
      path: '/master/models',
      color: 'text-violet-600',
      bgColor: 'bg-violet-50',
    },
    {
      label: 'Part Names',
      value: counts.part_names,
      icon: Wrench,
      path: '/master/part-names',
      color: 'text-amber-600',
      bgColor: 'bg-amber-50',
    },
    {
      label: 'Products',
      value: counts.products,
      icon: Package,
      path: '/products',
      color: 'text-sky-600',
      bgColor: 'bg-sky-50',
    },
    {
      label: 'Prasco Products',
      value: counts.prasco_products,
      icon: Boxes,
      path: '/prasco-products',
      color: 'text-indigo-600',
      bgColor: 'bg-indigo-50',
    },
    {
      label: 'Pending Product Inquiries',
      value: counts.pending_product_inquiries,
      icon: MessageSquare,
      path: '/inquiries/product',
      color: 'text-orange-600',
      bgColor: 'bg-orange-50',
      isPending: true,
    },
    {
      label: 'Pending Prasco Inquiries',
      value: counts.pending_prasco_inquiries,
      icon: MessageSquare,
      path: '/inquiries/prasco',
      color: 'text-rose-600',
      bgColor: 'bg-rose-50',
      isPending: true,
    },
    {
      label: 'Pending Leads',
      value: counts.pending_leads,
      icon: Users,
      path: '/leads',
      color: 'text-red-600',
      bgColor: 'bg-red-50',
      isPending: true,
    },
  ] : [];

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="animate-fade-in" data-testid="dashboard-page">
      <div className="mb-6">
        <h1 
          className="text-2xl font-bold text-slate-900"
          style={{ fontFamily: 'Manrope, sans-serif' }}
        >
          Dashboard Overview
        </h1>
        <p className="text-slate-500 mt-1">
          Quick view of your inventory and pending items
        </p>
      </div>

      <div className="stats-grid">
        {statCards.map((card) => (
          <div
            key={card.label}
            className={`stat-card ${card.isPending ? 'pending' : ''}`}
            onClick={() => navigate(card.path)}
            data-testid={`stat-card-${card.label.toLowerCase().replace(/\s+/g, '-')}`}
          >
            <div>
              <p className="text-sm text-slate-500 mb-1">{card.label}</p>
              <p className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                {card.value}
              </p>
              {card.isPending && card.value > 0 && (
                <div className="flex items-center gap-1 mt-2 text-amber-600">
                  <AlertCircle className="w-4 h-4" />
                  <span className="text-xs font-medium">Requires attention</span>
                </div>
              )}
            </div>
            <div className={`p-3 rounded-lg ${card.bgColor}`}>
              <card.icon className={`w-6 h-6 ${card.color}`} />
            </div>
          </div>
        ))}
      </div>

      {/* Quick Actions */}
      <div className="mt-8">
        <h2 
          className="text-lg font-semibold text-slate-900 mb-4"
          style={{ fontFamily: 'Manrope, sans-serif' }}
        >
          Quick Actions
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <button
            onClick={() => navigate('/products', { state: { openAdd: true } })}
            className="p-4 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors text-left"
            data-testid="quick-add-product"
          >
            <Package className="w-5 h-5 text-sky-600 mb-2" />
            <p className="font-medium text-slate-900">Add Product</p>
            <p className="text-sm text-slate-500">Create new product entry</p>
          </button>
          <button
            onClick={() => navigate('/prasco-products', { state: { openAdd: true } })}
            className="p-4 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors text-left"
            data-testid="quick-add-prasco"
          >
            <Boxes className="w-5 h-5 text-indigo-600 mb-2" />
            <p className="font-medium text-slate-900">Add Prasco Product</p>
            <p className="text-sm text-slate-500">Create new Prasco entry</p>
          </button>
          <button
            onClick={() => navigate('/inquiries/product')}
            className="p-4 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors text-left"
            data-testid="quick-view-inquiries"
          >
            <MessageSquare className="w-5 h-5 text-orange-600 mb-2" />
            <p className="font-medium text-slate-900">View Inquiries</p>
            <p className="text-sm text-slate-500">
              {counts?.pending_product_inquiries || 0} pending
            </p>
          </button>
          <button
            onClick={() => navigate('/leads')}
            className="p-4 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors text-left"
            data-testid="quick-view-leads"
          >
            <Users className="w-5 h-5 text-red-600 mb-2" />
            <p className="font-medium text-slate-900">View Leads</p>
            <p className="text-sm text-slate-500">
              {counts?.pending_leads || 0} pending
            </p>
          </button>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
